import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  CheckCircle2,
  ChevronDown,
  DollarSign,
  FlaskConical,
  LayoutGrid,
  MapPin,
  Pencil,
  Plus,
  Tag,
  Trash2,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { notificar } from '@/shared/ui/avisos';
import type { CategoriaTarifaria, ListaDeTarifas, Tarifa } from '@/shared/api/tipos';
import { Cargando } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { ConfirmarEliminacion } from '@/shared/ui/ConfirmarEliminacion';
import {
  CampoDelPanel,
  DosColumnas,
  PanelLateral,
  claseEntradaPanel,
} from '@/shared/ui/PanelLateral';
import { SimuladorDeTarifa } from './SimuladorDeTarifa';
import { PestanasDelTarifario } from './PestanasDelTarifario';
import { InsigniaActiva } from './InsigniaActiva';
import {
  RURALIDAD_ESTILO,
  RURALIDAD_ETIQUETA,
  formatearTasa,
  nombreLegible,
  tituloDeTarifa,
} from './nombres';

const esquema = z.object({
  tipoCliente: z.string().min(1, 'El tipo de cliente es obligatorio').max(100),
  categoria: z.string().max(100).optional(),
  ruralidad: z.enum(['urbano', 'rural', 'ambos']),
  descripcion: z.string().max(255).optional(),

  tasaPorcentual: z.string().optional(),
  valorBase: z.string().optional(),
  valor: z.string().optional(),

  rigeDesde: z.string().optional(),
});

type Formulario = z.infer<typeof esquema>;

export function PaginaTarifas() {
  const clienteDeConsultas = useQueryClient();

  const [editando, setEditando] = useState<Tarifa | null>(null);
  const [creando, setCreando] = useState(false);
  const [aEliminar, setAEliminar] = useState<Tarifa | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [simuladorAbierto, setSimuladorAbierto] = useState(false);

  const lista = useQuery({
    queryKey: ['tarifas'],
    queryFn: () => api.get<ListaDeTarifas>('/tarifas'),
  });

  const categorias = useQuery({
    queryKey: ['categorias-tarifarias'],
    queryFn: () => api.get<CategoriaTarifaria[]>('/categorias-tarifarias'),
  });

  function refrescar() {
    setError(null);
    void clienteDeConsultas.invalidateQueries({ queryKey: ['tarifas'] });
  }

  const cambiarEstado = useMutation({
    mutationFn: (t: Tarifa) =>
      api.put<Tarifa>(`/tarifas/${t.id}`, {
        tipoCliente: t.tipoCliente,
        categoria: t.categoria,
        ruralidad: t.ruralidad,
        descripcion: t.descripcion,
        tasa: t.tasa,
        valorBase: t.valorBase,
        rigeDesde: null,
        valor: t.valor,
        activa: !t.activa,
      }),
    onSuccess: refrescar,
    onError: (e) => {
      const mensaje = e instanceof ErrorDeApi ? e.message : 'No se pudo cambiar el estado';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const eliminar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/tarifas/${id}`),
    onSuccess: () => {
      setAEliminar(null);
      refrescar();
      notificar.exito('Tarifa eliminada');
    },
    onError: (e) => {
      const mensaje = e instanceof ErrorDeApi ? e.message : 'No se pudo eliminar';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const tarifas = lista.data?.tarifas ?? [];
  const activas = tarifas.filter((t) => t.activa).length;
  const valorDeReferencia = lista.data?.valorDeReferencia ?? 0;

  const tasaMaxima = Math.max(0, ...tarifas.map((t) => t.tasa ?? 0));

  const grupos = new Map<string, Tarifa[]>();
  for (const t of tarifas) {
    const clave = t.categoria ?? 'Sin categoría';
    grupos.set(clave, [...(grupos.get(clave) ?? []), t]);
  }

  return (
    <>
      <EncabezadoDePagina
        icono={Tag}
        color="ambar"
        titulo="Tarifas"
        subtitulo="Cuánto se le cobra al mes a cada tipo de cuenta"
        acciones={
          <button
            type="button"
            onClick={() => setCreando(true)}
            title="Nueva tarifa"
            aria-label="Nueva tarifa"
            className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
          >
            <Plus className="size-5" aria-hidden />
          </button>
        }
      />

      <PestanasDelTarifario />

      {error && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <Indicador
          icono={LayoutGrid}
          tono="azul"
          etiqueta="Total tarifas"
          valor={String(tarifas.length)}
          nota={`${grupos.size} ${grupos.size === 1 ? 'categoría' : 'categorías'}`}
        />
        <Indicador
          icono={CheckCircle2}
          tono="verde"
          etiqueta="Tarifas activas"
          valor={String(activas)}
          nota={`${tarifas.length - activas} inactivas`}
        />
        <Indicador
          icono={DollarSign}
          tono="ambar"
          etiqueta="Valor base"
          valor={`$${valorDeReferencia.toFixed(2)}`}
          nota="Base × tasa % = valor/mes"
        />
      </div>

      <div className="mb-6">
        <button
          type="button"
          onClick={() => setSimuladorAbierto((abierto) => !abierto)}
          aria-expanded={simuladorAbierto}
          className="flex w-full items-center gap-3 rounded-2xl border border-armazon-200 bg-white px-5 py-3 text-left shadow-sm transition-colors hover:bg-armazon-50"
        >
          <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-armazon-100 text-armazon-500">
            <FlaskConical className="size-4" aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[11px] font-semibold uppercase tracking-wider text-armazon-600">
              Probar una cuenta
            </span>
            <span className="block text-[10px] uppercase tracking-wide text-armazon-400">
              Con qué tarifa se le cobraría, antes de emitir la cartera
            </span>
          </span>
          <ChevronDown
            className={`size-4 shrink-0 text-armazon-400 transition-transform ${
              simuladorAbierto ? 'rotate-180' : ''
            }`}
            aria-hidden
          />
        </button>

        {simuladorAbierto && (
          <div className="mt-3">
            <SimuladorDeTarifa categorias={categorias.data ?? []} />
          </div>
        )}
      </div>

      {lista.isPending ? (
        <Cargando titulo="Cargando tarifas" detalle="Obteniendo registros" alto="h-72" />
      ) : tarifas.length === 0 ? (
        <div className="rounded-2xl border border-armazon-200 bg-white p-16 text-center">
          <Tag className="mx-auto mb-3 size-12 text-armazon-200" aria-hidden />
          <p className="text-sm font-medium uppercase text-armazon-500">
            No hay tarifas configuradas
          </p>
          <p className="mt-1 text-xs uppercase text-armazon-400">
            Sin ninguna, a todas las cuentas se les cobraría cero
          </p>
        </div>
      ) : (
        <div className="grid gap-5">
          {[...grupos.entries()].map(([categoria, items]) => (
            <section
              key={categoria}
              className="overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm"
            >
              <header className="flex items-center gap-3 border-b border-armazon-100 px-6 py-4">
                <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-amber-50 text-amber-600">
                  <Tag className="size-4" aria-hidden />
                </span>
                <div className="min-w-0">
                  <h2 className="truncate text-sm font-bold uppercase text-armazon-800">
                    {categoria}
                  </h2>
                  <p className="text-[11px] uppercase text-armazon-500">
                    {items.length}{' '}
                    {items.length === 1 ? 'tarifa configurada' : 'tarifas configuradas'}
                  </p>
                </div>
                <span className="ml-auto shrink-0 rounded-full bg-armazon-100 px-2.5 py-1 text-[11px] font-semibold uppercase text-armazon-600">
                  {items.filter((t) => t.activa).length}/{items.length} activas
                </span>
              </header>

              <div className="divide-y divide-armazon-100">
                {items.map((t) => (
                  <Fila
                    key={t.id}
                    tarifa={t}
                    tasaMaxima={tasaMaxima}
                    valorDeReferencia={valorDeReferencia}
                    alEditar={() => setEditando(t)}
                    alEliminar={() => setAEliminar(t)}
                    alAlternar={() => cambiarEstado.mutate(t)}
                    cambiando={cambiarEstado.isPending}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <FormularioDeTarifa
        key={editando?.id ?? 'nueva'}
        abierto={creando || editando !== null}
        tarifa={editando}
        categorias={categorias.data ?? []}
        valorDeReferencia={valorDeReferencia}
        alCerrar={() => {
          setCreando(false);
          setEditando(null);
        }}
        alGuardar={refrescar}
      />

      <ConfirmarEliminacion
        abierto={aEliminar !== null}
        alCerrar={() => {
          setAEliminar(null);
          setError(null);
        }}
        alConfirmar={() => aEliminar && eliminar.mutate(aEliminar.id)}
        eliminando={eliminar.isPending}
        titulo="Eliminar tarifa"
        descripcion="Las cuentas que casaban con ella pasarían a cobrar cero:"
        detalle={aEliminar ? tituloDeTarifa(aEliminar) : undefined}
        error={eliminar.isError ? error : null}
      />
    </>
  );
}

const TONO_INDICADOR: Record<string, string> = {
  azul: 'bg-blue-50 text-blue-600',
  verde: 'bg-emerald-50 text-emerald-600',
  ambar: 'bg-amber-50 text-amber-600',
};

function Indicador({
  icono: Icono,
  tono,
  etiqueta,
  valor,
  nota,
}: {
  icono: typeof Tag;
  tono: 'azul' | 'verde' | 'ambar';
  etiqueta: string;
  valor: string;
  nota: string;
}) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-armazon-200 bg-white px-5 py-4 shadow-sm">
      <span
        className={`grid size-11 shrink-0 place-items-center rounded-xl ${TONO_INDICADOR[tono]}`}
      >
        <Icono className="size-5" aria-hidden />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-armazon-500">
          {etiqueta}
        </p>
        <p className="importe text-2xl font-bold leading-tight text-armazon-900">{valor}</p>
        <p className="mt-0.5 text-[10px] uppercase tracking-wide text-armazon-400">{nota}</p>
      </div>
    </div>
  );
}

function Fila({
  tarifa,
  tasaMaxima,
  valorDeReferencia,
  alEditar,
  alEliminar,
  alAlternar,
  cambiando,
}: {
  tarifa: Tarifa;
  tasaMaxima: number;
  valorDeReferencia: number;
  alEditar: () => void;
  alEliminar: () => void;
  alAlternar: () => void;
  cambiando: boolean;
}) {
  const tasa = tarifa.tasa == null ? null : tarifa.tasa * 100;

  const base = tasa == null ? null : (tarifa.valorBase ?? valorDeReferencia);

  const ancho =
    tasa == null || tasaMaxima <= 0 ? 0 : Math.max(2, ((tarifa.tasa ?? 0) / tasaMaxima) * 100);

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-6 py-4 transition-colors hover:bg-armazon-50/60">
      <span
        className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase ${
          RURALIDAD_ESTILO[tarifa.ruralidad] ?? RURALIDAD_ESTILO['ambos']
        }`}
      >
        <MapPin className="size-2.5" aria-hidden />
        {RURALIDAD_ETIQUETA[tarifa.ruralidad] ?? tarifa.ruralidad}
      </span>

      <div className="min-w-0 flex-1 basis-48">
        <p className="truncate text-sm font-medium uppercase text-armazon-800">
          {tituloDeTarifa(tarifa)}
        </p>
        <p className="truncate text-[11px] uppercase tracking-wide text-armazon-400">
          {nombreLegible(tarifa.tipoCliente)}
        </p>
      </div>

      <div className="w-28 shrink-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[10px] uppercase tracking-wider text-armazon-400">Tasa</span>
          <span className="importe text-sm font-semibold text-indigo-600">
            {tasa == null ? '—' : `% ${formatearTasa(tasa)}`}
          </span>
        </div>
        <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-armazon-100">
          <div className="h-full rounded-full bg-indigo-500" style={{ width: `${ancho}%` }} />
        </div>
      </div>

      <div className="w-20 shrink-0 text-right">
        <p className="text-[10px] uppercase tracking-wider text-armazon-400">Base</p>
        <p className="importe text-xs font-medium text-armazon-600">
          {base == null ? '—' : `$${base.toFixed(2)}`}
        </p>
      </div>

      <div className="w-24 shrink-0 text-right">
        <p className="text-[10px] uppercase tracking-wider text-armazon-400">Valor / mes</p>
        <p className="importe text-lg font-bold leading-tight text-armazon-900">
          ${(tarifa.valor ?? 0).toFixed(2)}
        </p>
      </div>

      <InsigniaActiva
        activa={tarifa.activa}
        alAlternar={alAlternar}
        nombre={tituloDeTarifa(tarifa)}
        cambiando={cambiando}
      />

      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={alEditar}
          title="Editar"
          aria-label={`Editar ${tituloDeTarifa(tarifa)}`}
          className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
        >
          <Pencil className="size-[15px]" aria-hidden />
        </button>
        <button
          type="button"
          onClick={alEliminar}
          title="Eliminar"
          aria-label={`Eliminar ${tituloDeTarifa(tarifa)}`}
          className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-red-50 hover:text-alerta"
        >
          <Trash2 className="size-[15px]" aria-hidden />
        </button>
      </div>
    </div>
  );
}

function FormularioDeTarifa({
  abierto,
  tarifa,
  categorias,
  valorDeReferencia,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  tarifa: Tarifa | null;
  categorias: CategoriaTarifaria[];
  valorDeReferencia: number;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const editando = tarifa !== null;
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: {
      tipoCliente: tarifa?.tipoCliente ?? '',
      categoria: tarifa?.categoria ?? '',
      ruralidad: tarifa?.ruralidad ?? 'ambos',
      descripcion: tarifa?.descripcion ?? '',

      tasaPorcentual: tarifa?.tasa == null ? '' : String(tarifa.tasa * 100),
      valorBase: tarifa?.valorBase == null ? '' : String(tarifa.valorBase),
      valor: tarifa?.tasa == null && tarifa?.valor != null ? String(tarifa.valor) : '',
    },
  });

  const tasaPorcentual = watch('tasaPorcentual');
  const valorBase = watch('valorBase');

  const previa = (() => {
    const tasa = Number(tasaPorcentual);
    if (!tasaPorcentual || !Number.isFinite(tasa) || tasa === 0) {
      return null;
    }
    const base = valorBase && Number(valorBase) !== 0 ? Number(valorBase) : valorDeReferencia;
    return (base * (tasa / 100)).toFixed(2);
  })();

  const enviar = handleSubmit(async (datos) => {
    setErrorGeneral(null);
    try {
      const tasa = datos.tasaPorcentual ? Number(datos.tasaPorcentual) / 100 : null;

      const cuerpo = {
        tipoCliente: datos.tipoCliente,
        categoria: datos.categoria || null,
        ruralidad: datos.ruralidad,
        descripcion: datos.descripcion || null,
        tasa,
        valorBase: datos.valorBase ? Number(datos.valorBase) : null,

        valor: tasa ? null : datos.valor ? Number(datos.valor) : null,
        activa: tarifa?.activa ?? true,

        rigeDesde: datos.rigeDesde || null,
      };

      if (editando) {
        await api.put<Tarifa>(`/tarifas/${tarifa.id}`, cuerpo);
      } else {
        await api.post<Tarifa>('/tarifas', cuerpo);
      }

      alGuardar();
      alCerrar();
      notificar.exito(`Tarifa ${editando ? 'actualizada' : 'guardada'}`);
    } catch (causa) {
      setErrorGeneral(causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar la tarifa');
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar la tarifa');
    }
  });

  return (
    <PanelLateral
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={editando ? 'Editar tarifa' : 'Nueva tarifa'}
      descripcion={editando ? tituloDeTarifa(tarifa) : 'Cuánto se cobra al mes'}
      icono={Tag}
      alEnviar={enviar}
      enviando={isSubmitting}
    >
      {errorGeneral && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
        >
          {errorGeneral}
        </p>
      )}

      <CampoDelPanel
        etiqueta="Tipo de cliente"
        requerido
        error={errors.tipoCliente?.message}
        ayuda="Identificador único: bt_residencial_urbano"
      >
        <input {...register('tipoCliente')} className={`${claseEntradaPanel} importe`} autoFocus />
      </CampoDelPanel>

      <DosColumnas>
        <CampoDelPanel
          etiqueta="Categoría"
          error={errors.categoria?.message}
          ayuda="Con lo que busca el cálculo"
        >
          <select {...register('categoria')} className={claseEntradaPanel}>
            <option value="">Sin categoría</option>
            {categorias.map((c) => (
              <option key={c.id} value={c.nombre}>
                {c.nombre}
              </option>
            ))}
          </select>
        </CampoDelPanel>

        <CampoDelPanel etiqueta="Ruralidad" requerido error={errors.ruralidad?.message}>
          <select {...register('ruralidad')} className={claseEntradaPanel}>
            <option value="ambos">Ambos</option>
            <option value="urbano">Urbano</option>
            <option value="rural">Rural</option>
          </select>
        </CampoDelPanel>
      </DosColumnas>

      <CampoDelPanel etiqueta="Descripción" error={errors.descripcion?.message}>
        <input {...register('descripcion')} className={claseEntradaPanel} />
      </CampoDelPanel>

      <DosColumnas>
        <CampoDelPanel
          etiqueta="Tasa"
          error={errors.tasaPorcentual?.message}
          ayuda="En porcentaje: 0,75"
        >
          <input
            {...register('tasaPorcentual')}
            type="number"
            step="0.0001"
            min={0}
            className={`${claseEntradaPanel} importe`}
          />
        </CampoDelPanel>

        <CampoDelPanel
          etiqueta="Base propia"
          error={errors.valorBase?.message}
          ayuda={`Vacía usa $${valorDeReferencia.toFixed(2)}`}
        >
          <input
            {...register('valorBase')}
            type="number"
            step="0.01"
            min={0}
            className={`${claseEntradaPanel} importe`}
          />
        </CampoDelPanel>
      </DosColumnas>

      {previa ? (
        <div className="rounded-lg border border-marca-300 bg-marca-50 px-4 py-3">
          <p className="text-[11px] uppercase tracking-wide text-marca-700">Se cobraría al mes</p>
          <p className="importe text-lg font-bold text-marca-900">${previa}</p>
        </div>
      ) : (
        <CampoDelPanel
          etiqueta="Valor mensual"
          error={errors.valor?.message}
          ayuda="Sin tasa hay que escribirlo: una tarifa sin valor cobra cero"
        >
          <input
            {...register('valor')}
            type="number"
            step="0.01"
            min={0}
            className={`${claseEntradaPanel} importe`}
          />
        </CampoDelPanel>
      )}

      {editando && (
        <CampoDelPanel
          etiqueta="Rige desde"
          ayuda="Solo si cambia el importe. El valor anterior se conserva para lo ya facturado"
        >
          <input {...register('rigeDesde')} type="date" className={claseEntradaPanel} />
        </CampoDelPanel>
      )}
    </PanelLateral>
  );
}
